import React, { useEffect, useState } from "react";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/tanstack-react-start";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { PageTransition } from "~/components/page-transition";
import { getUserByClerkId, getUserPreferences } from "~/lib/functions/user";
import { coachDiscoverySteps } from "~/lib/schemas/coach-discovery";
import {
  UserPreferencesContext,
  UserPreferencesFormContext,
} from "~/contexts/user-preferences";
import { transformUserPreferencesToDiscoveryForm } from "~/lib/transforms/user-preferences";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { globalFormData } from "~/lib/hooks/use-discovery-form";

import type { UserPreferencesSelect } from "~/lib/schemas";

const defaultValues = {
  goals: [],
  otherGoal: undefined,
  startTime: "",
  support: "",
  coachingFormats: "",
  coachingApproaches: [],
  coachingPackages: [],
  coachGender: "",
  coachAges: [],
  userPersonality: [],
  coachPersonality: [],
  faithIntegration: [],
  preferredLanguages: ["English"],
  personalContext: "",
  sessionDeliveryMethods: "",
  prefersCoachLocation: "No preference",
  coachLocationRegion: undefined,
  coachLocationCountry: undefined,
  otherFaithIntegration: undefined,
};

export const getUserPreferencesServerFn = createServerFn({ method: "GET" })
  .validator(z.object({ userId: z.string() }))
  .handler(async ({ data }) => {
    const { userId } = data;

    const user = await getUserByClerkId({ data: { clerkUserId: userId } });
    if (!userId || !user) {
      return defaultValues;
    }

    const userPreferences = await getUserPreferences({
      data: { userId: user.id },
    });
    if (!userPreferences) {
      return defaultValues;
    }
    return transformUserPreferencesToDiscoveryForm(userPreferences);
  });

export const Route = createFileRoute("/_public/discovery/steps")({
  loader: async ({ context }) => {
    // The userId will be determined in the component based on auth state
    // Just ensure we have a baseline query for empty userId (public case)
    const defaultUserId = "";

    await context.queryClient.ensureQueryData({
      queryKey: ["userPreferences", defaultUserId],
      queryFn: () =>
        getUserPreferencesServerFn({ data: { userId: defaultUserId } }),
    });
    return {};
  },
  component: DiscoveryStepsLayout,
});

function DiscoveryStepsLayout() {
  const location = useLocation();
  const auth = useAuth();

  // Use actual user ID if logged in, otherwise empty for public discovery
  const userId = auth.userId || "";

  const { data: userPreferences } = useQuery({
    queryKey: ["userPreferences", userId],
    queryFn: () => getUserPreferencesServerFn({ data: { userId } }),
  });

  const [preferences, setPreferences] = useState<UserPreferencesSelect | null>(
    null,
  );

  const [formState, setFormState] = useState({
    steps: Array(10).fill(false),
    currentStepIndex: 0,
  });

  const setStepCompleted = (stepIndex: number) => {
    setFormState((prev) => ({
      ...prev,
      steps: prev.steps.map((completed, index) =>
        index === stepIndex ? true : completed,
      ),
    }));
  };

  const isShowProgressBar = !location.pathname.includes("processing");

  useEffect(() => {
    const match = location.pathname.match(/\/discovery\/steps\/(\d+)/);
    if (match && match[1]) {
      const stepNumber = parseInt(match[1], 10);
      setFormState((prev) => ({ ...prev, currentStepIndex: stepNumber - 1 }));
    }

    // Update preferences with proper priority: localStorage form data > saved preferences > defaults
    if (userPreferences) {
      // Try to get form data from localStorage first (for page refresh)
      let formData = globalFormData;
      if ("undefined" !== typeof window && 0 === Object.keys(formData).length) {
        try {
          const stored = localStorage.getItem("discoveryFormData");
          if (stored) {
            formData = JSON.parse(stored);
            // Update globalFormData with localStorage data
            Object.assign(globalFormData, formData);
          }
        } catch {
          // Ignore JSON parse errors - use empty object as fallback
        }
      }

      const mergedPreferences = {
        id: userId || "",
        createdAt: null,
        updatedAt: null,
        userId: userId || "",
        preferencesEmbedding: [],
        ...userPreferences,
        ...formData, // Form data takes priority
        otherGoal: formData.otherGoal || userPreferences.otherGoal || null,
        coachLocationCountry:
          formData.coachLocationCountry ||
          userPreferences.coachLocationCountry ||
          null,
        coachLocationRegion:
          formData.coachLocationRegion ||
          userPreferences.coachLocationRegion ||
          null,
        otherFaithIntegration:
          formData.otherFaithIntegration ||
          userPreferences.otherFaithIntegration ||
          null,
      };

      setPreferences(mergedPreferences);
    }
  }, [location.pathname, userPreferences]);

  return (
    <UserPreferencesContext.Provider value={preferences}>
      <UserPreferencesFormContext.Provider
        value={{ ...formState, setStepCompleted }}
      >
        <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
          <Breadcrumb
            className="mb-6"
            items={[
              { label: "Home", href: "/" },
              { label: "Find my dream coach", href: "/discovery" },
            ]}
          />
          {isShowProgressBar && (
            <DiscoveryStep.Progress
              totalSteps={coachDiscoverySteps.length}
              currentIndex={formState.currentStepIndex}
            />
          )}
          <div className="relative w-full overflow-visible">
            <PageTransition>
              <Outlet />
            </PageTransition>
          </div>
        </div>
      </UserPreferencesFormContext.Provider>
    </UserPreferencesContext.Provider>
  );
}
